import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/vi";

const PaymentProfileModal = ({
  open,
  onClose,
  onSave,
  contractId,
  contractNumber,
  contractName,
  editingProfile = null,
}) => {
  const [formData, setFormData] = useState({
    paymentName: "",
    paymentDate: dayjs(),
    vat: 10,
    notes: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingProfile) {
      setFormData({
        paymentName: editingProfile.paymentName || "",
        paymentDate: editingProfile.paymentDate
          ? dayjs(editingProfile.paymentDate)
          : dayjs(),
        vat: editingProfile.vat || 10,
        notes: editingProfile.notes || "",
      });
    } else {
      setFormData({
        paymentName: "",
        paymentDate: dayjs(),
        vat: 10,
        notes: "",
      });
    }
    setErrors({});
  }, [editingProfile, open]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.paymentName.trim()) {
      newErrors.paymentName = "Tên kỳ thanh toán không được để trống";
    }

    if (!formData.paymentDate) {
      newErrors.paymentDate = "Ngày thanh toán không được để trống";
    }

    if (formData.vat < 0 || formData.vat > 100) {
      newErrors.vat = "VAT phải từ 0% đến 100%";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const paymentProfileData = {
        contractId: contractId,
        paymentName: formData.paymentName.trim(),
        paymentDate: formData.paymentDate.format("YYYY-MM-DD"),
        vat: parseInt(formData.vat),
        notes: formData.notes.trim() || null,
      };

      await onSave(paymentProfileData, editingProfile?.paymentProfileId);
      onClose();
    } catch (error) {
      console.error("Error saving payment profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      paymentName: "",
      paymentDate: dayjs(),
      vat: 10,
      notes: "",
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "none",
          border: "1px solid #e0e0e0",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          fontSize: "1.25rem",
          fontWeight: 600,
          fontFamily: "Inter, sans-serif",
        }}
      >
        {editingProfile ? "Chỉnh sửa kỳ thanh toán" : "Thêm kỳ thanh toán mới"}
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        {/* Contract Info */}
        <Box sx={{ mb: 3, p: 2, bgcolor: "#f8f9fa", borderRadius: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Thông tin hợp đồng
          </Typography>
          <Typography variant="body2">
            <strong>Số hợp đồng:</strong> {contractNumber}
          </Typography>
          <Typography variant="body2">
            <strong>Tên hợp đồng:</strong> {contractName}
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Tên kỳ thanh toán"
              value={formData.paymentName}
              onChange={(e) => handleChange("paymentName", e.target.value)}
              error={!!errors.paymentName}
              helperText={errors.paymentName}
              required
              placeholder="VD: Thanh toán lần 1, Tạm ứng, Nghiệm thu..."
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#e0e0e0" },
                  "&:hover fieldset": { borderColor: "#b0b0b0" },
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="vi">
              <DatePicker
                label="Ngày thanh toán"
                value={formData.paymentDate}
                onChange={(date) => handleChange("paymentDate", date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.paymentDate,
                    helperText: errors.paymentDate,
                    required: true,
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#e0e0e0" },
                        "&:hover fieldset": { borderColor: "#b0b0b0" },
                      },
                    },
                  },
                }}
                format="DD/MM/YYYY"
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl
              fullWidth
              error={!!errors.vat}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#e0e0e0" },
                  "&:hover fieldset": { borderColor: "#b0b0b0" },
                },
              }}
            >
              <InputLabel>VAT (%)</InputLabel>
              <Select
                value={formData.vat}
                label="VAT (%)"
                onChange={(e) => handleChange("vat", e.target.value)}
              >
                <MenuItem value={0}>0%</MenuItem>
                <MenuItem value={5}>5%</MenuItem>
                <MenuItem value={8}>8%</MenuItem>
                <MenuItem value={10}>10%</MenuItem>
                <MenuItem value={15}>15%</MenuItem>
                <MenuItem value={20}>20%</MenuItem>
              </Select>
              {errors.vat && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 1.5 }}
                >
                  {errors.vat}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Ghi chú"
              value={formData.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              multiline
              rows={3}
              placeholder="Ghi chú về kỳ thanh toán này..."
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#e0e0e0" },
                  "&:hover fieldset": { borderColor: "#b0b0b0" },
                },
              }}
            />
          </Grid>
        </Grid>

        {Object.keys(errors).length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Vui lòng kiểm tra lại thông tin đã nhập
          </Alert>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontFamily: "Inter, sans-serif",
            borderColor: "#e0e0e0",
            color: "#666",
            "&:hover": {
              borderColor: "#b0b0b0",
              backgroundColor: "#f5f5f5",
            },
          }}
        >
          Hủy
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{
            textTransform: "none",
            fontFamily: "Inter, sans-serif",
            backgroundColor: "#1976d2",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#1565c0",
              boxShadow: "none",
            },
          }}
        >
          {loading ? "Đang lưu..." : editingProfile ? "Cập nhật" : "Thêm mới"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentProfileModal;
